<template>
  <div class="mc-template-item-table">
    <div class="clearfix">
      <div class="d-flex mc-list-header">
        <h4 class="card-title flex-grow-1">Detail Template</h4>
        <McButton
          v-if="$route.params.id && selectedIdsLength"
          variant="secondary-red"
          class="mx-2 ms-auto"
          icon="icon-outline_trash"
          :text="$t('global.delete')"
          @click="$refs['mc-modal-delete'].show()"
        />

        <McButton
          v-if="$route.params.id"
          variant="primary"
          class="ms-auto"
          text="Tambah"
          icon="icon-fill_plus"
          :disabled="disabledGlobal"
          @click="handleAdd"
        />
      </div>
    </div>
    <b-card>
      <div v-if="!$route.params.id" class="d-flex justify-content-center">
        <div class="d-flex w-auto gap-3">
          <div>
            <img src="@/assets/images/no-data-illustration.png" width="150" alt />
          </div>
          <div class="d-flex flex-column justify-content-center gap-1">
            <strong class="primary-disabled-text">{{
              $t('setting.list.fleetTaskCost.detail')
            }}</strong>
            <p class="secondary-disabled-text">
              {{ $t('global.action.save') }}
              <span class="blue-disabled-text">{{ $t('global.information') }}</span>
              {{ $t('global.object.firstText') }}
            </p>
          </div>
        </div>
      </div>
      <div v-else>
        <b-row align-h="end">
          <b-col md="8" xl="10">
            <h6 class="mc-row-check mt-0" v-if="selectedIdsLength">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              {{ $t('global.object.selectedRow') }}
              <a @click="handleUnselectAll" class="color-primary">{{
                $t('global.action.deselect')
              }}</a>
            </h6>
            <McAlert
              v-if="invalidKeysLength && !selectedIdsLength"
              variant="error"
              text="Baris data error."
            >
              <template #prefix>
                <div class="mc-alert__custom_prefix">
                  <p>{{ invalidKeysLength }}</p>
                </div>
              </template>
            </McAlert>
          </b-col>
        </b-row>

        <FormulateForm
          v-model="formValues"
          name="editable-table-form"
          ref="formRef"
          :errors="formulateFormErrors"
          @submit="handleSubmit"
          @failed-validation="handleFailedValidation"
        >
          <div class="table-responsive border border-1 mc-template-item-table__editable-table">
            <b-table
              :busy="isBusy"
              :items="items"
              :fields="computedFields"
              fixed
              show-empty
              foot-clone
              class="editable-table"
            >
              <!-- Loading -->
              <template #table-busy>
                <div class="__busy">
                  <b-spinner class="align-middle mb-2" />
                  <strong>{{ $t('global.text.loading') }}...</strong>
                </div>
              </template>

              <!-- Empty Result -->
              <template #empty>
                <div class="text-center my-4">{{ $t('global.text.noData') }}</div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <div class="form-check" style="width: 10px">
                  <b-form-checkbox
                    @change="handleSelectAll"
                    v-model="selectedAll"
                    :disabled="disabledGlobal"
                  />
                </div>
              </template>

              <!-- Slot: Header Data -->
              <template #head()="data">
                <div
                  class="d-flex"
                  @click="handleSortChange(data)"
                  :class="
                    data.field.name === 'amount' ? 'justify-content-end' : 'justify-content-between'
                  "
                >
                  {{ data.label }}
                  <template v-if="data.field.isSortable">
                    <div>
                      <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                        <i class="mc-icon icon-outline_chevron-up"></i>
                      </div>
                      <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                        <i class="mc-icon icon-outline_chevron-down"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <div class="form-check">
                  <b-form-checkbox
                    @change="(event) => handleSelect(event, data)"
                    :checked="selectedIds[data.item.id]"
                    :disabled="disabledGlobal"
                  />
                </div>
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ index, field, value, item }">
                <template v-if="field.isEditable && isEditingRow == index">
                  <FormulateInput
                    v-bind="{ ...field }"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :initialObject="
                      field.getInitialObject && field.name === 'activity_id'
                        ? field.getInitialObject(activityObj)
                        : undefined
                    "
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    validation-behavior="live"
                    error-behavior="value"
                    size="sm"
                    :ref="`${field.name}-ref`"
                  />
                </template>

                <!-- Show Only Column -->
                <template v-else-if="!field.isEditable && isEditingRow == index">
                  <div>
                    {{ showOnlyValue(value) }}
                  </div>
                </template>

                <template v-else>
                  <div
                    :class="{
                      'td__overflow-ellipsis': field.name === 'activity_id',
                      'td__invalid-data':
                        invalidItems[item.id] && invalidItems[item.id][field.name],
                    }"
                  >
                    {{ transformCellValue(value, field) }}
                  </div>
                </template>
              </template>

              <!-- Slot: Action Column (Last Column) -->
              <template #cell(action)="data">
                <div v-if="isEditingRow == data.index">
                  <b-button variant="outline" class="__accept-button btn" type="submit">
                    <i class="mc-icon icon-fill_checkmark"></i>
                  </b-button>

                  <b-button
                    variant="outline"
                    class="__cancel-button btn"
                    @click="handleEditCancel(data)"
                  >
                    <i class="mc-icon icon-outline_close"></i>
                  </b-button>
                </div>

                <b-button
                  v-else
                  variant="outline"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: isEditingRow !== null || isBusy,
                  }"
                  @click="handleEditStart(data)"
                >
                  <i class="mc-icon icon-outline_edit"></i>
                </b-button>
              </template>

              <template #foot()="{ column }">
                <div v-if="column === 'activity.name'" class="mc-footer">Total</div>
                <div v-if="column === 'amount'" class="mc-footer d-flex justify-content-end">
                  {{ totalAmount }}
                </div>
              </template>
            </b-table>
          </div>
        </FormulateForm>
      </div>

      <b-modal
        class="mc-modal"
        id="mc-modal-delete"
        ref="mc-modal-delete"
        title="Hapus"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
            {{ $t('global.confirmDelete2') }}
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.action.delete')" @click="handleDelete" />
          </div>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import { sum } from '@/helpers/calculate'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import templateItemFields from '../../lib/fields/create/items'

export default {
  components: {
    McButton,
    McAlert,
  },
  props: {
    apiClient: { required: true },
    status: { type: String },
    invalidFields: { type: Array },
  },
  data() {
    return {
      isBusy: true,
      isSubmitting: false,
      templateItemFields,
      ids: [],
      items: [],
      sorts: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      isEditingRow: null,
      search: '',
      formValues: {},
      templateItemValues: {},
      showResponse: {},
      activityObj: {},
      invalidItems: {},
      formulateFormErrors: {},
    }
  },
  computed: {
    disabledGlobal() {
      return this.isEditingRow !== null || this.isBusy
    },
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
          thStyle: 'width: 25px',
        },
        ...this.templateItemFields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
    totalAmount() {
      let totalAmount = 0

      this.items.forEach((item) => {
        if (isNaN(item.amount)) return
        totalAmount = sum(totalAmount ?? 0, Number(item.amount))
      })

      return totalAmount.toLocaleString() ?? '-'
    },
    invalidKeysLength() {
      return Object.keys(this.invalidItems).length
    },
  },
  watch: {
    async formValues() {
      if (this.isEditingRow == null && !this.invalidKeysLength) return

      // get the field that is currently changing
      const [patchData, hasChanged] = this.filterUnchangedData(
        this.formValues,
        this.templateItemValues
      )

      if (!hasChanged) return

      const changedFields = Object.keys(patchData)
      changedFields.forEach((field) => {
        if (!this.formulateFormErrors[field]) return

        // delete the field in formulateFormErrors
        this.$delete(this.formulateFormErrors, field)
      })
    },
    'formValues.amount': function (newValue, oldValue) {
      if (this.isEditingRow === null) return

      // Clear amount value
      if (newValue == '' && !newValue) return

      // Same value
      if (newValue == oldValue) return

      // If user input valid quantity
      this.$set(this.items[this.isEditingRow], 'amount', newValue)
    },
  },
  async mounted() {
    await this.getData({
      item: true,
    })
  },
  methods: {
    handleFailedValidation() {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    showOnlyValue(value) {
      return value || '-'
    },
    transformCellValue(value) {
      if (value === false) return `-`
      if (typeof value === 'number') return `${value.toLocaleString()}`
      return value
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    AssignInvalidErrors() {
      if (!this.invalidFields.length) return
      const indexObject = {}

      // format the invalidFields from array to object with id as the key
      for (const item of this.invalidFields) {
        const templateItemObj = {}

        for (const field in item) {
          if (field === 'id') continue
          templateItemObj[field] = ['Data telah dihapus dari sistem.']
        }

        indexObject[item.id] = templateItemObj
      }

      // assign to invalidItems as state that could be used to show error
      this.invalidItems = indexObject
    },
    async getData(params) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      const data = await this.apiClient.get(this.$route.params.id, params)

      if (!data) return
      this.items = data
      this.totalCount = data.length
      this.ids = data.map((val) => val.id)
      this.isBusy = false
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.show(this.$route.params.id, data.item.id)
      if (!refetchedData) return

      const templateItemData = refetchedData.data

      // assign formValues with response show data
      this.templateItemFields.forEach((field) => {
        if (!field.isEditable) return
        let value

        // assign for the activity_id
        if (field.name === 'activity_id') value = templateItemData.activity.id
        else value = templateItemData[field.name]

        this.$set(this.formValues, field.name, value)
      })

      // assign activityObj with activity object in response show data
      this.activityObj = { ...templateItemData.activity }

      this.isEditingRow = data.index
      this.showResponse = { ...templateItemData }
      this.templateItemValues = { ...this.formValues }

      // assign the form errors from invalidItems with the current id
      if (this.invalidItems[templateItemData.id]) {
        this.formulateFormErrors = { ...this.invalidItems[templateItemData.id] }
      }

      await this.$nextTick()
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formulateFormErrors = {}
      this.activityObj = {}

      this.isEditingRow = null
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)
      if (!createdData) return false

      const refetchedData = await this.apiClient.show(this.$route.params.id, createdData.data.id)
      if (!refetchedData) return false

      let detailData = refetchedData.data

      this.ids.push(refetchedData.data.id)
      this.$mcToast.success('Data berhasil ditambahkan')

      return {
        ...data,
        ...detailData,
      }
    },
    async submitUpdate(data, oldData) {
      const [patchData, hasChanged] = this.filterUnchangedData(data, this.templateItemValues)
      if (!hasChanged) {
        this.isSubmitting = false
        this.handleEditCancel({ index: this.isEditingRow })
        return
      }

      let detailData

      if (hasChanged) {
        const updatedData = await this.apiClient.update(
          this.$route.params.id,
          oldData.id,
          patchData
        )
        if (!updatedData) return false

        const refetchedData = await this.apiClient.show(this.$route.params.id, oldData.id)
        if (!refetchedData) return false
        detailData = refetchedData.data
      }

      if (this.invalidItems[oldData.id]) {
        this.$delete(this.invalidItems, oldData.id)
        this.formulateFormErrors = {}
      }

      this.$mcToast.success(this.$t('global.updateSuccess'))

      return {
        ...detailData,
      }
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (Object.keys(this.formulateFormErrors).length) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.templateItemFields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }
      // If newData is false, it means there is an error
      if (newData === false) {
        this.isSubmitting = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.activityObj = {}
      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        if (newData[key] == oldData[key]) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const promises = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.delete(key))
        }
      }
      await Promise.all(promises)
      if (this.search) this.handleClearSearch()
      await this.getData({
        item: true,
      })

      // Delete data in invalid items
      for (const selectedid in this.selectedIds) {
        if (this.invalidItems[selectedid]) {
          this.$delete(this.invalidItems, selectedid)
        }
      }

      await this.$nextTick()
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Data berhasil dihapus')
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.items = [{ id: 0 }, ...this.items]
    },
    // TODO: under consideration, same with CostTable component
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
